import React from 'react';

export const SM_ANNUAL_FEE = 4499;
export const TAMP_BPS = 35;
export const AUM_MIN = 5_000_000;
export const AUM_MAX = 250_000_000;
export const AUM_STEP = 1_000_000;
export const AUM_DEFAULT = 20_000_000;

export function formatAum(n) {
  if (n >= 1_000_000_000) {
    const b = n / 1_000_000_000;
    return `$${b % 1 === 0 ? b.toFixed(0) : b.toFixed(1)}B`;
  }
  const m = n / 1_000_000;
  return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
}

export function formatUsd(n) {
  return Math.max(0, Math.round(n)).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export default function AumSavingsCard({ aum, onChange, id = 'aum-slider' }) {
  const tampCost = aum * (TAMP_BPS / 10_000);
  const savings = Math.max(0, tampCost - SM_ANNUAL_FEE);
  const savingsPct = tampCost > 0 ? Math.round((savings / tampCost) * 100) : 0;

  return (
    <div className="rounded-2xl border border-bg-300 bg-bg-200 p-6 sm:p-8 lg:sticky lg:top-8">
      <p className="text-xs font-semibold uppercase tracking-widest text-primary-100">
        What you keep
      </p>
      <h2 className="mt-2 text-xl font-bold text-text-100">
        Slide your AUM. See the savings.
      </h2>
      <p className="mt-2 text-sm text-text-200">
        Typical TAMPs charge {TAMP_BPS} bps. SharpeMetrix is a flat {formatUsd(SM_ANNUAL_FEE)} / year.
      </p>

      <div className="mt-8">
        <div className="flex items-end justify-between mb-3">
          <label htmlFor={id} className="text-sm font-medium text-text-200">
            Assets under management
          </label>
          <span className="text-2xl font-bold text-text-100 tabular-nums">{formatAum(aum)}</span>
        </div>
        <input
          id={id}
          type="range"
          min={AUM_MIN}
          max={AUM_MAX}
          step={AUM_STEP}
          value={aum}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-bg-300 accent-primary-100"
        />
        <div className="mt-2 flex justify-between text-xs text-text-300">
          <span>{formatAum(AUM_MIN)}</span>
          <span>{formatAum(AUM_MAX)}</span>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        <div className="flex items-center justify-between rounded-xl bg-bg-100 px-4 py-3">
          <span className="text-sm text-text-200">Typical TAMP cost</span>
          <span className="text-sm font-semibold text-text-100 tabular-nums">{formatUsd(tampCost)}/yr</span>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-bg-100 px-4 py-3">
          <span className="text-sm text-text-200">SharpeMetrix</span>
          <span className="text-sm font-semibold text-text-100 tabular-nums">{formatUsd(SM_ANNUAL_FEE)}/yr</span>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-signal-positive/10 ring-1 ring-signal-positive/20 px-4 py-4">
          <span className="text-sm font-medium text-text-100">You save</span>
          <div className="text-right">
            <div className="text-xl font-bold text-signal-positive tabular-nums">{formatUsd(savings)}</div>
            <div className="text-xs text-signal-positive/80">{savingsPct}% less per year</div>
          </div>
        </div>
      </div>

      <p className="mt-6 text-xs text-text-300 leading-relaxed">
        Illustration only, using a {TAMP_BPS} bps TAMP/SMA fee versus our annual plan.
        Your actual savings depend on the fee you pay today.
      </p>
    </div>
  );
}
